import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { getActiveCoupons, getActiveSubscriptions, subMatchesCoupon } from "../Services/campaignService"

const API_BASES = (() => {
  const host = window.location.hostname;
  if (host === "crm.whytefarms.com") return ["https://app.whytefarms.com"];      // CRM domain -> app.whytefarms
  if (host === "3.22.224.17") return ["http://13.207.176.232:3000"];             // 3.22 IP -> 13.207 IP
  return ["https://app.whytefarms.com", "http://13.207.176.232:3000"];           // localhost / baaki: pehle domain, phir IP
})();
const API_PATH = '/api/customers/campaign-details'
const REFERRAL_PATH = '/api/referrals/code' // + /<referralCode>
// Apply coupon manually: pehle validate, phir redeem
// (coupons aur subscriptions Firestore se aate hain: campaigns + subscriptions_data)
const VALIDATE_PATH = '/api/coupons/validate' // POST { couponCode, customerId } -> { eligible, sourceRef, discountData, message }
const REDEEM_PATH = '/api/coupons/redeem'     // POST { sourceRef, sourceType, appliedVia, agentUid, agentReason, benefitAmount, ... }

const fetchWithFallback = async (path, options) => {
  let lastErr
  for (const base of API_BASES) {
    try {
      const res = await fetch(`${base}${path}`, options)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res
    } catch (e) {
      lastErr = e
      console.warn(`[fetch] ${base}${path} failed:`, e.message)
    }
  }
  throw lastErr
}


const postJson = async (path, body, bases = API_BASES) => {
  let bestFail
  let lastErr
  for (const base of bases) {
    try {
      const res = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      console.log(`[post] ${base}${path}`, res.status, json)
      const out = { ok: res.ok && json?.success !== false, status: res.status, json, base }
      if (out.ok) return out
      // 404 ("code mila hi nahi") ke mukable doosre server ka asli jawab zyada kaam ka hai
      if (!bestFail || (bestFail.status === 404 && out.status !== 404)) bestFail = out
    } catch (e) {
      lastErr = e
      console.warn(`[post] ${base}${path} failed:`, e.message)
    }
  }
  if (bestFail) return bestFail
  throw lastErr
}
// ---- Shared style tokens ----
const colors = {
  bg: '#F7F5F1',
  card: '#FFFFFF',
  border: '#E7E3DB',
  text: '#1F1D1A',
  subtext: '#8A8578',
  label: '#9C978A',
  green: '#2F5233',
  greenBg: '#E7F0E3',
  blue: '#2B4C6F',
  blueBg: '#E7EEF5',
  purpleBg: '#F1ECFA',
  purpleText: '#6B4FA0',
  orange: '#B5651D',
  divider: '#EFEBE3',
  inputBorder: '#D8D3C8',
  buttonGreen: '#2F4B32',
}

const sectionWrap = {
  background: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: '20px 24px',
  marginBottom: 20,
  boxSizing: 'border-box',
}

const sectionTitle = {
  fontSize: 16,
  fontWeight: 600,
  color: colors.text,
  margin: '0 0 16px 0',
}

const pill = (bg, fg) => ({
  display: 'inline-block',
  background: bg,
  color: fg,
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
})

// ---- Grid "table" building blocks (no native <table>, so no external
// stylesheet — zebra striping, hover states, etc — can bleed in) ----
const GridTable = ({ columns, children }) => (
  <div style={{ width: '100%', background: '#fff' }}>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        columnGap: 12,
        background: '#fff',
      }}
    >
      {children}
    </div>
  </div>
)

const HeadCell = ({ children, align }) => (
  <div
    style={{
      background: '#fff',
      textAlign: align || 'left',
      fontSize: 11,
      fontWeight: 600,
      color: colors.label,
      letterSpacing: '0.04em',
      paddingBottom: 10,
      borderBottom: `1px solid ${colors.divider}`,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </div>
)

const Cell = ({ children, align, last, style }) => (
  <div
    style={{
      background: '#fff',
      textAlign: align || 'left',
      fontSize: 14,
      color: colors.text,
      padding: '14px 0',
      borderBottom: last ? 'none' : `1px solid ${colors.divider}`,
      ...style,
    }}
  >
    {children}
  </div>
)

// ---- API helpers ----
const pick = (...v) => v.find((x) => x !== undefined && x !== null && x !== '')
const arr = (o, ...keys) => {
  for (const k of keys) if (Array.isArray(o?.[k])) return o[k]
  return []
}
const cap = (x) => (x ? String(x).charAt(0).toUpperCase() + String(x).slice(1) : '—')
const money = (v) =>
  v === undefined || v === null || v === '' || isNaN(Number(v))
    ? v ?? '—'
    : `₹${Number(v).toLocaleString('en-IN')}`
const fmtDate = (v) => {
  const d = v?.toDate ? v.toDate() : v ? new Date(v) : null
  return d && !isNaN(d)
    ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'
}
const discountText = (c) => {
  const label = pick(c.discountLabel, c.discountText, c.discount)
  if (label !== undefined && isNaN(Number(label))) return String(label)
  const val = pick(c.discountValue, c.discount, c.config?.discountValue)
  const type = pick(c.discountType, c.config?.discountType)
  if (val === undefined) return '—'
  return type === 'percent' || type === 'percentage' ? `${val}%` : `₹${val}`
}

// Referral block -> { referral: card values, referrals: popup list }.
// `ref` is the referral object (or array of referees), `root` the whole payload
const normalizeReferral = (ref = {}, root = {}) => {
  const list = Array.isArray(ref)
    ? ref
    : arr(ref, 'referrals', 'referred', 'referees', 'referredCustomers', 'list', 'customers')
  return {
    referral: {
      code: String(pick(ref.referralCode, ref.code, ref.referral_code, root.referralCode, root.code, '—')),
      referredBy: String(
        pick(ref.referredBy, ref.referredByName, ref.referrerName, ref.referrer?.name, root.referredBy, '—')
      ),
      count: pick(
        ref.customersReferred, ref.totalReferred, ref.totalReferrals, ref.referredCount, ref.referralCount,
        list.length, 0
      ),
      successful: pick(
        ref.successfulReferrals, ref.successful, ref.successCount, ref.successfulCount,
        list.filter((r) => /success|qualif|complete/i.test(String(r.status ?? r.state ?? ''))).length, 0
      ),
      earned: money(pick(ref.rewardsEarned, ref.totalRewards, ref.totalRewardEarned, ref.rewardEarned, 0)),
    },
    referrals: list.map((r) => {
      const status = String(pick(r.status, r.state, '—'))
      return {
        name: String(pick(r.customerName, r.name, r.refereeName, r.referee, '—')),
        phone: String(pick(r.phone, r.mobile, r.customerId, r.refereeId, '')),
        date: fmtDate(pick(r.date, r.createdAt, r.referredAt)),
        status: cap(status),
        successful: /success|qualif|complete/i.test(status),
        reward: money(pick(r.reward, r.rewardAmount, r.referrerReward, '—')),
      }
    }),
  }
}

// Turns the raw API response into the shapes the three tables + referral card use
const normalize = (json) => {
  const root = json?.data ?? json ?? {}
  const all = arr(root, 'campaigns', 'items', 'list')
  const ofType = (...t) => all.filter((c) => t.includes(c.type))

  let coupons = arr(root, 'coupons', 'appliedCoupons', 'availableCoupons', 'couponList')
  if (!coupons.length) coupons = ofType('coupon')
  let offers = arr(root, 'offers', 'offersUsed', 'usedOffers', 'offerList')
  if (!offers.length) offers = ofType('offer')
  let cashbacks = arr(root, 'walletCashback', 'walletCashbacks', 'cashbacks', 'cashback', 'rewards')
  if (!cashbacks.length) cashbacks = ofType('wallet_cashback', 'referral_reward')

  const { referral, referrals } = normalizeReferral(
    root.referral ?? root.referralInfo ?? root.referrals ?? {},
    root
  )

  return {
    coupons: coupons.map((c) => ({
      name: String(pick(c.campaignName, c.name, c.title, '—')),
      code: String(pick(c.campaignCode, c.campaignId, c.id, '')),
      discount: discountText(c),
      appliedTo: String(pick(c.appliedTo, c.productName, c.product, c.subscription, '—')),
      appliedVia: String(pick(c.appliedViaLabel, c.appliedVia, c.applyMode, '—')),
      status: cap(pick(c.status, 'Available')),
    })),
    offers: offers.map((o) => ({
      name: String(pick(o.campaignName, o.offerName, o.name, o.title, '—')),
      date: fmtDate(pick(o.date, o.usedAt, o.startDate, o.createdAt, o.appliedAt)),
      benefit: String(pick(o.benefitReceived, o.benefit, o.benefitText, o.reward, '—')),
      schedule: String(pick(o.schedule, o.scheduleText, o.status, '—')),
    })),
    cashbacks: cashbacks.map((w) => ({
      campaign: String(pick(w.campaignName, w.campaign, w.name, w.title, '—')),
      recharge: money(pick(w.rechargeAmount, w.recharge)),
      credit: money(pick(w.promoCredit, w.credit, w.creditGiven, w.amount)),
      unused: money(pick(w.unused, w.unusedCredit, w.unusedAmount)),
      date: fmtDate(pick(w.date, w.createdAt, w.creditedAt)),
    })),
    referral,
    referrals,
  }
}

const EmptyRow = ({ text }) => (
  <div style={{ gridColumn: '1 / -1', padding: '18px 0', fontSize: 13.5, color: colors.subtext }}>
    {text}
  </div>
)

const CustomerOffer = () => {
  const [showReferrals, setShowReferrals] = useState(false)
  const { id } = useParams()
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refData, setRefData] = useState(null) // from /api/referrals/code/<code>
  const [reloadKey, setReloadKey] = useState(0) // coupon apply hone ke baad data dobara laane ke liye

  useEffect(() => {
    if (!id) return undefined
    let off = false
    setLoading(true)
    setError('')
    fetchWithFallback(`${API_PATH}?customerId=${encodeURIComponent(id)}`)
      .then((res) => res.json())
      .then((json) => {
        console.log('[customer campaign-details] raw response:', json)
        if (json?.success === false) throw new Error(json.message || 'Request failed')
        if (!off) setInfo(normalize(json))
      })
      .catch((e) => {
        console.error('Could not load offers & rewards: ' + e.message)
        if (!off) setError(e.message)
      })
      .finally(() => !off && setLoading(false))
    return () => {
      off = true
    }
  }, [id, reloadKey])

  // Referral information: GET /api/referrals/code/<referralCode>.
  // Uses the code from campaign-details when it has one, otherwise the customer id.
  useEffect(() => {
    if (!id || loading) return undefined
    const fromDetails = info?.referral?.code
    const code = fromDetails && fromDetails !== '—' ? fromDetails : id
    let off = false
    fetchWithFallback(`${REFERRAL_PATH}/${encodeURIComponent(code)}`)
      .then((res) => res.json())
      .then((json) => {
        console.log('[referral code] raw response:', json)
        if (json?.success === false) throw new Error(json.message || 'Request failed')
        const root = json?.data ?? json ?? {}
        if (!off) setRefData(normalizeReferral(root.referral ?? root, root))
      })
      .catch((e) => {
        console.error('Could not load referral information: ' + e.message)
        if (!off) setRefData(null)
      })
    return () => {
      off = true
    }
  }, [id, loading, info])

  const d = info || { coupons: [], offers: [], cashbacks: [], referral: {}, referrals: [] }
  // referral API wins; campaign-details is only a fallback
  const ref = refData?.referral ?? d.referral
  const referralsList = refData?.referrals ?? d.referrals
  const emptyText = loading ? 'Loading...' : error ? `Could not load data (${error})` : null

  return (
    <div
      style={{
        background: 'transparent',
        width: '100%',
        boxSizing: 'border-box',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.7fr 1fr',
          gap: 24,
          width: '100%',
        }}
      >
        {/* ---------------- LEFT COLUMN ---------------- */}
        <div>
          {/* Available / applied coupons */}
          <div style={sectionWrap}>
            <h4 style={sectionTitle}>Available / applied coupons</h4>
            <GridTable columns="2.2fr 0.8fr 1.6fr 1.2fr 0.9fr">
              <HeadCell>Campaign</HeadCell>
              <HeadCell>Discount</HeadCell>
              <HeadCell>Applied To</HeadCell>
              <HeadCell>Applied Via</HeadCell>
              <HeadCell align="right">Status</HeadCell>
              {d.coupons.length === 0 && <EmptyRow text={emptyText || 'No coupons available or applied.'} />}
              {d.coupons.map((c, i) => {
                const last = i === d.coupons.length - 1
                const applied = c.status === 'Applied'
                return (
                  <React.Fragment key={i}>
                    <Cell last={last}>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: colors.subtext, marginTop: 2 }}>{c.code}</div>
                    </Cell>
                    <Cell last={last}>{c.discount}</Cell>
                    <Cell last={last}>{c.appliedTo}</Cell>
                    <Cell last={last}>{c.appliedVia}</Cell>
                    <Cell last={last} align="right">
                      <span style={pill(applied ? colors.greenBg : colors.blueBg, applied ? colors.green : colors.blue)}>
                        {c.status}
                      </span>
                    </Cell>
                  </React.Fragment>
                )
              })}
            </GridTable>
          </div>

          {/* Offers used */}
          <div style={sectionWrap}>
            <h4 style={sectionTitle}>Offers used</h4>
            <GridTable columns="2fr 1fr 1.4fr 1.4fr">
              <HeadCell>Offer</HeadCell>
              <HeadCell>Date</HeadCell>
              <HeadCell>Benefit Received</HeadCell>
              <HeadCell align="right">Schedule</HeadCell>
              {d.offers.length === 0 && <EmptyRow text={emptyText || 'No offers used yet.'} />}
              {d.offers.map((o, i) => {
                const last = i === d.offers.length - 1
                return (
                  <React.Fragment key={i}>
                    <Cell last={last}>{o.name}</Cell>
                    <Cell last={last}>{o.date}</Cell>
                    <Cell last={last}>{o.benefit}</Cell>
                    <Cell last={last} align="right">{o.schedule}</Cell>
                  </React.Fragment>
                )
              })}
            </GridTable>
          </div>

          {/* Wallet cashback */}
          <div style={{ ...sectionWrap, marginBottom: 0 }}>
            <h4 style={sectionTitle}>Wallet cashback</h4>
            <GridTable columns="1.8fr 1fr 1.1fr 0.8fr 1fr">
              <HeadCell>Campaign</HeadCell>
              <HeadCell>Recharge Amount</HeadCell>
              <HeadCell align="right">Promo Credit</HeadCell>
              <HeadCell align="right">Unused</HeadCell>
              <HeadCell align="right">Date</HeadCell>
              {d.cashbacks.length === 0 && <EmptyRow text={emptyText || 'No wallet cashback yet.'} />}
              {d.cashbacks.map((w, i) => {
                const last = i === d.cashbacks.length - 1
                return (
                  <React.Fragment key={i}>
                    <Cell last={last}>{w.campaign}</Cell>
                    <Cell last={last}>{w.recharge}</Cell>
                    <Cell last={last} align="right" style={{ color: colors.orange, fontWeight: 600 }}>{w.credit}</Cell>
                    <Cell last={last} align="right">{w.unused}</Cell>
                    <Cell last={last} align="right">{w.date}</Cell>
                  </React.Fragment>
                )
              })}
            </GridTable>
          </div>
        </div>

        {/* ---------------- RIGHT COLUMN ---------------- */}
        <div>
          {/* Referral information */}
          <div style={{ ...sectionWrap, marginBottom: 0 }}>
            <h4 style={sectionTitle}>Referral information</h4>

            <div
              style={{
                background: colors.purpleBg,
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 12, color: colors.purpleText, marginBottom: 4 }}>
                Referral code
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.text,
                  letterSpacing: '0.03em',
                }}
              >
                {ref.code || '—'}
              </div>
              <div style={{ fontSize: 12, color: colors.subtext, marginTop: 4 }}>
                {ref.code && ref.code !== '—' ? `whytefarms.in/r/${ref.code}` : ''}
              </div>
            </div>

            <InfoRow label="Referred by" value={ref.referredBy || '—'} />
            <InfoRow label="Customers referred" value={String(ref.count ?? 0)} />
            <InfoRow label="Successful referrals" value={String(ref.successful ?? 0)} />
            <InfoRow label="Rewards earned" value={ref.earned || '₹0'} valueColor={colors.orange} last />

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setShowReferrals(true)
              }}
              style={{
                display: 'inline-block',
                marginTop: 14,
                fontSize: 13,
                fontWeight: 600,
                color: colors.text,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              View her referrals →
            </a>
          </div>

          {/* Apply coupon manually (referral card ke neeche) */}
          <ApplyCouponCard
            customerId={id}
            onApplied={() => setReloadKey((k) => k + 1)}
          />
        </div>
      </div>

      {/* Referrals popup */}
      {showReferrals && (
        <div
          onClick={() => setShowReferrals(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 10,
              width: '100%',
              maxWidth: 520,
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '18px 22px',
                borderBottom: `1px solid ${colors.divider}`,
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.text }}>
                  Customers referred
                </h4>
                <div style={{ fontSize: 12.5, color: colors.subtext, marginTop: 2 }}>
                  {referralsList.length} people referred using code {ref.code || '—'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowReferrals(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 20,
                  lineHeight: 1,
                  color: colors.subtext,
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ padding: '10px 22px 20px 22px' }}>
              {referralsList.map((r, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 0',
                    borderBottom:
                      idx === referralsList.length - 1 ? 'none' : `1px solid ${colors.divider}`,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: 12.5, color: colors.subtext, marginTop: 2 }}>
                      {r.phone} · {r.date}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span
                      style={pill(
                        r.successful ? colors.greenBg : colors.blueBg,
                        r.successful ? colors.green : colors.blue
                      )}
                    >
                      {r.status}
                    </span>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: colors.orange,
                        marginTop: 6,
                      }}
                    >
                      {r.reward}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- small reusable pieces ----
const InfoRow = ({ label, value, valueColor, last }) => (
  <div
    style={{
      background: '#fff',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: last ? 'none' : `1px solid ${colors.divider}`,
    }}
  >
    <span style={{ fontSize: 13.5, color: colors.subtext }}>{label}</span>
    <span style={{ fontSize: 13.5, fontWeight: 600, color: valueColor || colors.text }}>
      {value}
    </span>
  </div>
)

// ---- Apply coupon manually (same card design as Referral information) ----
const fieldLabel = {
  display: 'block',
  fontSize: 12,
  color: colors.subtext,
  margin: '14px 0 6px 0',
}
const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  fontSize: 13.5,
  color: colors.text,
  background: '#fff',
  border: `1px solid ${colors.inputBorder}`,
  borderRadius: 8,
  outline: 'none',
  fontFamily: 'inherit',
}
const shortDate = (v) => {
  const d = v?.toDate ? v.toDate() : v ? new Date(v) : null
  return d && !isNaN(d) ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''
}

// Redeem API ko benefitAmount chahiye: backend ka discountAmount, warna fixed value, warna subscription price ka %
const calcBenefit = (cfg = {}, sub = {}) => {
  const amt = cfg?.discountAmount
  if (amt !== null && amt !== undefined && amt !== '' && !isNaN(Number(amt))) return Number(amt)
  const val = Number(cfg?.discountValue || 0)
  if (cfg?.discountType === 'fixed') return val
  const price = Number(sub?.price || 0)
  return Math.round(price * val) / 100
}

// Subscription dropdown ka text: "A2 Cow Ghee · #11678583 · starts 29 Oct"
const subLabel = (s = {}) => {
  const name = s.product_name ?? s.productName ?? s.name ?? s.subscription_type ?? 'Subscription'
  const start = shortDate(s.start_date ?? s.startDate)
  return `${name} · #${s.subscription_id ?? s.id}${start ? ` · starts ${start}` : ''}`
}

// Coupon apply hone ke baad dikhne wala popup
const SuccessPopup = ({ data, onClose }) => {
  if (!data) return null
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 400,
          padding: '28px 26px 22px',
          textAlign: 'center',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: colors.greenBg,
            color: colors.green,
            fontSize: 34,
            fontWeight: 700,
            lineHeight: '64px',
            margin: '0 auto 14px',
          }}
        >
          ✓
        </div>
        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text }}>Coupon applied</h4>
        <div style={{ fontSize: 13.5, color: colors.subtext, marginTop: 6 }}>{data.message}</div>

        <div style={{ textAlign: 'left', marginTop: 18, fontSize: 13.5 }}>
          {[
            ['Coupon', data.coupon],
            ['Subscription', data.subscription],
          ].map(([k, v], i) => (
            <div
              key={k}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 0',
                borderTop: i === 0 ? `1px solid ${colors.divider}` : 'none',
                borderBottom: `1px solid ${colors.divider}`,
              }}
            >
              <span style={{ color: colors.subtext }}>{k}</span>
              <span style={{ fontWeight: 600, color: colors.text, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 20,
            width: '100%',
            padding: '11px 16px',
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background: colors.buttonGreen,
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          OK
        </button>
      </div>
    </div>
  )
}

const ApplyCouponCard = ({ customerId, onApplied }) => {
  const [options, setOptions] = useState([]) // sirf ACTIVE coupon campaigns (campaigns collection se)
  const [subs, setSubs] = useState([]) // sirf ACTIVE subscriptions (subscriptions_data se)
  const [couponCode, setCouponCode] = useState('') // '' = "Select coupon"
  const [subId, setSubId] = useState('') // '' = "Select subscription"
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null) // { ok, text } (sirf error ke liye)
  const [popup, setPopup] = useState(null) // success popup

  // Active coupon campaigns
  useEffect(() => {
    let off = false
    getActiveCoupons()
      .then((list) => !off && setOptions(list))
      .catch((e) => {
        console.error('Could not load active coupons: ' + e.message)
        if (!off) setOptions([])
      })
    return () => {
      off = true
    }
  }, [])

  // Customer ke active subscriptions
  useEffect(() => {
    if (!customerId) return undefined
    let off = false
    getActiveSubscriptions(customerId)
      .then((list) => !off && setSubs(list))
      .catch((e) => {
        console.error('Could not load active subscriptions: ' + e.message)
        if (!off) setSubs([])
      })
    return () => {
      off = true
    }
  }, [customerId])

  // Selected coupon ke product ki hi subscriptions chalengi
  const selectedCoupon = options.find((o) => o.code === couponCode)
  const eligibleSubs = selectedCoupon ? subs.filter((s) => subMatchesCoupon(s, selectedCoupon)) : []
  const needsProduct = !!selectedCoupon?.productIds?.length
  const productLabel = selectedCoupon?.productNames?.length
    ? selectedCoupon.productNames.join(', ')
    : needsProduct
      ? 'selected product'
      : ''

  // Coupon badalne par subscription wapas "Select subscription" par (ya agar purani ab eligible nahi)
  useEffect(() => {
    if (subId && !eligibleSubs.some((s) => String(s.id) === subId)) setSubId('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCode, subs, options])

  // Coupon ya subscription badalne par purana result hata do
  useEffect(() => {
    setResult(null)
  }, [couponCode, subId])

  // Step 1: /api/coupons/validate  ->  Step 2: /api/coupons/redeem
  const submit = async () => {
    if (!reason.trim()) {
      setResult({ ok: false, text: 'Please enter a reason.' })
      return
    }
    setBusy(true)
    setResult(null)
    try {
      // 1. Validate: coupon code + customer. Server sourceRef aur discount details deta hai
      const v = await postJson(VALIDATE_PATH, { couponCode, customerId: String(customerId) })
      if (!v.ok || v.json?.eligible === false) {
        throw new Error(v.json?.message || v.json?.error || `Validate failed (HTTP ${v.status})`)
      }
      const { sourceRef, discountData } = v.json
      if (!sourceRef) throw new Error('The validate API did not return a sourceRef.')

      // 2. Redeem: usi server par jahan validate pass hua (stage / production mix na ho)
      const sub = subs.find((s) => String(s.id) === String(subId)) || {}
      const r = await postJson(
        REDEEM_PATH,
        {
          sourceRef,
          sourceType: 'subscription',
          appliedVia: 'agent',
          agentUid: localStorage.getItem('userId') || 'unknown',
          agentReason: reason.trim(),
          benefitAmount: calcBenefit(discountData, sub),
          revenueAttributed: 0,
          status: 'applied',
        },
        [v.base]
      )
      if (!r.ok) {
        throw new Error(r.json?.message || r.json?.error || `Redeem failed (HTTP ${r.status})`)
      }

      // Success: popup dikhao, form reset karo
      setPopup({
        message: r.json?.message || 'Coupon applied successfully.',
        coupon: `${selectedCoupon?.name || ''} · ${couponCode}`,
        subscription: subLabel(sub),
      })
      setCouponCode('')
      setSubId('')
      setReason('')
      onApplied && onApplied()
    } catch (e) {
      setResult({ ok: false, text: e.message })
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = !busy && couponCode && subId && reason.trim()

  return (
    <div style={{ ...sectionWrap, marginTop: 20, marginBottom: 0 }}>
      <SuccessPopup data={popup} onClose={() => setPopup(null)} />

      <h4 style={sectionTitle}>Apply coupon manually</h4>

      <label style={{ ...fieldLabel, marginTop: 0 }}>Coupon</label>
      <select style={fieldStyle} value={couponCode} onChange={(e) => setCouponCode(e.target.value)}>
        <option value="">{options.length ? 'Select coupon' : 'No active coupons'}</option>
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.name} · {o.code}
            {o.productNames?.length ? ` · ${o.productNames.join(', ')}` : ''}
          </option>
        ))}
      </select>

      {selectedCoupon && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: colors.subtext }}>
          Valid for: <b style={{ color: colors.text }}>{needsProduct ? productLabel : 'all products'}</b>
        </div>
      )}

      <label style={fieldLabel}>Subscription</label>
      <select
        style={fieldStyle}
        value={subId}
        disabled={!selectedCoupon}
        onChange={(e) => setSubId(e.target.value)}
      >
        <option value="">
          {!selectedCoupon
            ? 'Select coupon first'
            : eligibleSubs.length === 0
              ? needsProduct
                ? 'No active subscription for this product'
                : 'No active subscriptions'
              : 'Select subscription'}
        </option>
        {eligibleSubs.map((s) => (
          <option key={String(s.id)} value={String(s.id)}>
            {subLabel(s)}
          </option>
        ))}
      </select>

      {selectedCoupon && eligibleSubs.length === 0 && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 12.5,
            lineHeight: 1.4,
            background: '#FBEAEA',
            color: '#9B2C2C',
          }}
        >
          {needsProduct
            ? `This customer has no active subscription for ${productLabel}, so this coupon cannot be applied.`
            : 'This customer has no active subscription.'}
        </div>
      )}

      <label style={fieldLabel}>Reason</label>
      <input
        type="text"
        style={fieldStyle}
        value={reason}
        placeholder="e.g. customer called, code failed"
        onChange={(e) => setReason(e.target.value)}
      />

      {result && !result.ok && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#9B2C2C' }}>{result.text}</div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        style={{
          marginTop: 16,
          width: '100%',
          padding: '11px 16px',
          fontSize: 14,
          fontWeight: 600,
          color: '#fff',
          background: colors.buttonGreen,
          border: 'none',
          borderRadius: 8,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          opacity: canSubmit ? 1 : 0.5,
        }}
      >
        {busy ? 'Applying...' : 'Apply coupon'}
      </button>
    </div>
  )
}

export default CustomerOffer